import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import GenericModal from '../../../../ui/GenericModal';
import { Button, CircularProgress } from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import type { KidLayoutLevel } from '../../../../../api/kids/types';
import { useAllFundManagersForClient } from '../../../../../api/rs3-public/raptorPlatform';
import {
  inputClassName,
  inputErrorClassName,
} from '../../../utilities/TemplateManagement/templateManagement.constants';
import { cn } from '../../../../../utilities/cn';
import {
  extractDetail,
  isConflictError,
} from '../../../../../utilities/QueryHelpers';


type SaveLayoutFormValues = {
  layoutName: string;
  layoutLevel: KidLayoutLevel;
  fundManagerId?: string;
};

export type SaveLayoutSubmission = {
  layoutName: string;
  layoutLevel: KidLayoutLevel;
  layoutValue: string;
};

type Props = {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  clientId: string;
  isSaving: boolean;
  onSubmit: (values: SaveLayoutSubmission) => Promise<void>;
};

export default function SaveLayoutForm({
  isOpen,
  setIsOpen,
  clientId,
  isSaving,
  onSubmit,
}: Props) {

  const { data: allFundManagers, isLoading: allFundManagersIsLoading, error: allFundManagersError } = useAllFundManagersForClient(clientId);


  const {
    register,
    handleSubmit,
    watch,
    reset,
    setError,
    clearErrors,
    setValue,
    formState: { errors },
  } = useForm<SaveLayoutFormValues>({
    defaultValues: {
      layoutName: '',
      layoutLevel: 'client',
      fundManagerId: '',
    },
    mode: 'onChange',
  });

  const layoutLevel = watch('layoutLevel');
  const layoutName = watch('layoutName');
  const fundManagerId = watch('fundManagerId');

  const {
    onBlur: onLayoutNameBlur,
    onChange: onLayoutNameChange,
    ...layoutNameField
  } = register('layoutName', {
    validate: (value) =>
      value.trim().length > 0 || 'Please enter a template name.',
    setValueAs: (value) => (typeof value === 'string' ? value.trim() : value),
  });

  useEffect(() => {
    if (!isOpen) return;
    reset({
      layoutName: '',
      layoutLevel: 'client',
      fundManagerId: '',
    });
  }, [isOpen, reset]);

  const submit = async (values: SaveLayoutFormValues) => {
    const layoutValue =
      values.layoutLevel === 'fund_manager'
        ? values.fundManagerId || ''
        : clientId;

    try {
      await onSubmit({
        layoutName: values.layoutName,
        layoutLevel: values.layoutLevel,
        layoutValue,
      });
      setIsOpen(false);
    } catch (error) {
      if (!isConflictError(error)) return;

      setError('layoutName', {
        type: 'server',
        message:
          extractDetail(error) ??
          'A template with this name already exists.',
      });
    }
  };

  const isSaveDisabled =
    isSaving ||
    !layoutName.trim() ||
    (layoutLevel === 'fund_manager' && !fundManagerId);

  return (
    <GenericModal
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      title="Save Layout"
      description="Create a reusable KID layout from the current editor form."
      className="max-w-lg"
    >
      <form className="space-y-4" onSubmit={handleSubmit(submit)}>
        <div>
          <label
            htmlFor="layout-name"
            className="mb-1 block text-xs font-medium text-gray-700"
          >
            Layout Name
          </label>
          <input
            id="layout-name"
            type="text"
            placeholder="Layout Name"
            className={cn(
              inputClassName,
              errors.layoutName && inputErrorClassName,
            )}
            {...layoutNameField}
            onChange={(event) => {
              if (errors.layoutName?.type === 'server') {
                clearErrors('layoutName');
              }
              onLayoutNameChange(event);
            }}
            onBlur={(event) => {
              onLayoutNameBlur(event);
              const trimmed = event.target.value.trim();
              if (trimmed !== event.target.value) {
                setValue('layoutName', trimmed, { shouldValidate: true });
              }
            }}
          />
          {errors.layoutName?.message ? (
            <p className="mt-1 text-xs text-red-600">{errors.layoutName.message}</p>
          ) : null}
        </div>

        <div>
          <label
            htmlFor="layout-level"
            className="mb-1 block text-xs font-medium text-gray-700"
          >
            Type
          </label>
          <select
            id="layout-level"
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-mainblue focus:outline-none focus:ring-1 focus:ring-mainblue"
            {...register('layoutLevel', { required: true })}
          >
            <option value="client">Client</option>
            <option value="sub_client">Sub-client</option>
            <option value="fund_manager">Fund manager</option>
          </select>
        </div>

        {layoutLevel === 'fund_manager' && (
          <div>
            <label
              htmlFor="layout-fund-manager"
              className="mb-1 block text-xs font-medium text-gray-700"
            >
              Fund manager
            </label>
            {allFundManagersIsLoading ? (
              <div className="py-2 text-gray-500 text-sm">
                Loading fund managers...
              </div>
            ) : allFundManagersError ? (
              <div className="py-2 text-red-600 text-sm">
                Failed to load fund managers. Please try again.
              </div>
            ) : (
              <select
                id="layout-fund-manager"
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-mainblue focus:outline-none focus:ring-1 focus:ring-mainblue"
                {...register('fundManagerId', {
                  validate: (v, allValues) =>
                    allValues.layoutLevel !== 'fund_manager' ||
                    (!!v && v.length > 0) ||
                    'Please select a fund manager for this layout.',
                })}
              >
                <option value="">Select a fund manager</option>
                {allFundManagers?.filter((fm) => fm.fund_manager_is_active)
                  .sort((a, b) => a.fund_manager_name.localeCompare(b.fund_manager_name))
                  .map((fm) => (
                    <option key={fm.fund_manager_id} value={fm.fund_manager_id}>
                      {fm.fund_manager_name}
                    </option>
                  ))}
              </select>
            )}
            {errors.fundManagerId?.message && (
              <p className="mt-1 text-xs text-red-600">
                {errors.fundManagerId.message}
              </p>
            )}
          </div>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <Button
            onClick={() => setIsOpen(false)}
            disabled={isSaving}
            variant="outlined"
            size="small"
            className="normal-case"
            type="button"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={isSaveDisabled}
            variant="contained"
            disableElevation
            size="small"
            className="normal-case !text-white bg-mainblue hover:bg-mainblue/90 disabled:!bg-mainblue disabled:!opacity-40"
            startIcon={
              isSaving ? (
                <CircularProgress size={12} color="inherit" />
              ) : (
                <SaveIcon style={{ fontSize: 14 }} />
              )
            }
          >
            Save Layout
          </Button>
        </div>
      </form>
    </GenericModal>
  );
}

